import React, { useState, useRef, useEffect } from 'react';
import {
    View, StyleSheet, Text, TouchableOpacity, ScrollView,
    Image, Animated, Dimensions, Platform, Linking
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { H1, H2, H3, Body1, Body2, Caption } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

const MENTOR_PRIMARY = '#7c3aed';

const MentorDetailScreen = () => {
    const insets = useSafeAreaInsets();
    const navigation = useNavigation();
    const route = useRoute();
    const mentor = route.params?.mentor;

    const scrollY = useRef(new Animated.Value(0)).current;
    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
    }, []);

    if (!mentor) {
        return (
            <View style={styles.container}>
                <Text>Mentor not found</Text>
            </View>
        );
    }

    const handleContact = (type) => {
        switch (type) {
            case 'email':
                if (mentor.email) Linking.openURL(`mailto:${mentor.email}`);
                break;
            case 'phone':
                if (mentor.phone) Linking.openURL(`tel:${mentor.phone}`);
                break;
            case 'instagram':
                if (mentor.socialLinks?.instagram) Linking.openURL(mentor.socialLinks.instagram);
                break;
            case 'twitter':
                if (mentor.socialLinks?.twitter) Linking.openURL(mentor.socialLinks.twitter);
                break;
            case 'linkedin':
                if (mentor.socialLinks?.linkedin) Linking.openURL(mentor.socialLinks.linkedin);
                break;
        }
    };

    const headerScale = scrollY.interpolate({
        inputRange: [-100, 0],
        outputRange: [1.2, 1],
        extrapolateRight: 'clamp',
    });

    return (
        <View style={styles.container}>
            <Animated.ScrollView
                style={styles.scrollView}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{ paddingBottom: insets.bottom + 100 }}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: true }
                )}
                scrollEventThrottle={16}
            >
                {/* Hero Section */}
                <Animated.View style={[styles.heroSection, { transform: [{ scale: headerScale }] }]}>
                    <LinearGradient
                        colors={['#4c1d95', '#5b21b6', '#7c3aed']}
                        style={StyleSheet.absoluteFill}
                    />
                    <View style={styles.heroDecorOrb1} />
                    <View style={styles.heroDecorOrb2} />
                    <View style={styles.heroDecorOrb3} />

                    <View style={[styles.heroContent, { paddingTop: insets.top + 16 }]}>
                        {/* Top bar */}
                        <View style={styles.topBar}>
                            <TouchableOpacity
                                style={styles.backBtn}
                                onPress={() => navigation.goBack()}
                            >
                                <Ionicons name="arrow-back" size={22} color="#FFFFFF" />
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.shareBtn}>
                                <Ionicons name="share-outline" size={22} color="#FFFFFF" />
                            </TouchableOpacity>
                        </View>

                        {/* Profile */}
                        <View style={styles.profileContainer}>
                            <View style={styles.avatarOuterRing}>
                                <Image
                                    source={{ uri: mentor.profileImageUrl || 'https://i.pravatar.cc/300' }}
                                    style={styles.avatar}
                                />
                                <View style={styles.onlineDot} />
                            </View>
                            <Text style={styles.mentorName}>{mentor.name}</Text>
                            <View style={styles.typeBadge}>
                                <Ionicons name="star" size={12} color="#FBBF24" />
                                <Text style={styles.typeText}>{mentor.mentorType || 'Mentor'}</Text>
                            </View>

                            {/* Quick stats */}
                            <View style={styles.quickStats}>
                                <View style={styles.quickStatItem}>
                                    <Text style={styles.quickStatValue}>{mentor.points || 0}</Text>
                                    <Text style={styles.quickStatLabel}>Points</Text>
                                </View>
                                <View style={styles.quickStatDivider} />
                                <View style={styles.quickStatItem}>
                                    <Text style={styles.quickStatValue}>Active</Text>
                                    <Text style={styles.quickStatLabel}>Status</Text>
                                </View>
                                <View style={styles.quickStatDivider} />
                                <View style={styles.quickStatItem}>
                                    <Text style={styles.quickStatValue}>4.8</Text>
                                    <Text style={styles.quickStatLabel}>Rating</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                </Animated.View>

                {/* Content */}
                <Animated.View style={[styles.contentSurface, { opacity: fadeAnim }]}>
                    {/* Action Buttons */}
                    <View style={styles.actionRow}>
                        <TouchableOpacity
                            style={styles.primaryActionBtn}
                            onPress={() => handleContact('email')}
                        >
                            <LinearGradient
                                colors={['#7c3aed', '#6d28d9']}
                                style={styles.primaryActionGradient}
                            >
                                <Ionicons name="send" size={18} color="#FFFFFF" />
                                <Text style={styles.primaryActionText}>Send Message</Text>
                            </LinearGradient>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={styles.secondaryActionBtn}
                            onPress={() => navigation.navigate('MentorBooking', { mentor })}
                        >
                            <Ionicons name="calendar-outline" size={18} color={MENTOR_PRIMARY} />
                            <Text style={styles.secondaryActionText}>Book Session</Text>
                        </TouchableOpacity>
                    </View>

                    {/* About Section */}
                    {mentor.mentorBio && (
                        <View style={styles.section}>
                            <View style={styles.sectionHeader}>
                                <View style={[styles.sectionIcon, { backgroundColor: '#7c3aed12' }]}>
                                    <Ionicons name="document-text-outline" size={18} color={MENTOR_PRIMARY} />
                                </View>
                                <Text style={styles.sectionTitle}>About</Text>
                            </View>
                            <Text style={styles.bioText}>{mentor.mentorBio}</Text>
                        </View>
                    )}

                    {/* Expertise Section */}
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <View style={[styles.sectionIcon, { backgroundColor: '#10B98112' }]}>
                                <Ionicons name="ribbon-outline" size={18} color="#10B981" />
                            </View>
                            <Text style={styles.sectionTitle}>Expertise</Text>
                        </View>
                        <View style={styles.expertiseTags}>
                            {(mentor.mentorType || 'Mentoring').split(' ').map((tag, i) => (
                                <View key={i} style={styles.expertiseTag}>
                                    <Text style={styles.expertiseTagText}>{tag}</Text>
                                </View>
                            ))}
                            <View style={styles.expertiseTag}>
                                <Text style={styles.expertiseTagText}>Youth Empowerment</Text>
                            </View>
                            <View style={styles.expertiseTag}>
                                <Text style={styles.expertiseTagText}>Leadership</Text>
                            </View>
                        </View>
                    </View>

                    {/* Contact Information */}
                    <View style={styles.section}>
                        <View style={styles.sectionHeader}>
                            <View style={[styles.sectionIcon, { backgroundColor: '#3B82F612' }]}>
                                <Ionicons name="chatbubbles-outline" size={18} color="#3B82F6" />
                            </View>
                            <Text style={styles.sectionTitle}>Get in Touch</Text>
                        </View>

                        <View style={styles.contactCards}>
                            {mentor.email && (
                                <TouchableOpacity
                                    style={styles.contactCard}
                                    onPress={() => handleContact('email')}
                                >
                                    <View style={[styles.contactCardIcon, { backgroundColor: '#EF444412' }]}>
                                        <Ionicons name="mail" size={20} color="#EF4444" />
                                    </View>
                                    <View style={styles.contactCardInfo}>
                                        <Text style={styles.contactCardLabel}>Email</Text>
                                        <Text style={styles.contactCardValue} numberOfLines={1}>{mentor.email}</Text>
                                    </View>
                                    <Ionicons name="open-outline" size={16} color="#D1D5DB" />
                                </TouchableOpacity>
                            )}

                            {mentor.phone && (
                                <TouchableOpacity
                                    style={styles.contactCard}
                                    onPress={() => handleContact('phone')}
                                >
                                    <View style={[styles.contactCardIcon, { backgroundColor: '#10B98112' }]}>
                                        <Ionicons name="call" size={20} color="#10B981" />
                                    </View>
                                    <View style={styles.contactCardInfo}>
                                        <Text style={styles.contactCardLabel}>Phone</Text>
                                        <Text style={styles.contactCardValue}>{mentor.phone}</Text>
                                    </View>
                                    <Ionicons name="open-outline" size={16} color="#D1D5DB" />
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>

                    {/* Social Links */}
                    {(mentor.socialLinks?.instagram || mentor.socialLinks?.twitter || mentor.socialLinks?.linkedin) && (
                        <View style={styles.section}>
                            <View style={styles.sectionHeader}>
                                <View style={[styles.sectionIcon, { backgroundColor: '#EC489912' }]}>
                                    <Ionicons name="globe-outline" size={18} color="#EC4899" />
                                </View>
                                <Text style={styles.sectionTitle}>Social Media</Text>
                            </View>
                            <View style={styles.socialGrid}>
                                {mentor.socialLinks?.instagram && (
                                    <TouchableOpacity
                                        style={styles.socialCard}
                                        onPress={() => handleContact('instagram')}
                                    >
                                        <LinearGradient
                                            colors={['#f09433', '#e6683c', '#dc2743', '#cc2366', '#bc1888']}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={styles.socialCardGradient}
                                        >
                                            <Ionicons name="logo-instagram" size={22} color="#FFFFFF" />
                                        </LinearGradient>
                                        <Text style={styles.socialCardLabel}>Instagram</Text>
                                    </TouchableOpacity>
                                )}
                                {mentor.socialLinks?.twitter && (
                                    <TouchableOpacity
                                        style={styles.socialCard}
                                        onPress={() => handleContact('twitter')}
                                    >
                                        <View style={[styles.socialCardGradient, { backgroundColor: '#1DA1F2' }]}>
                                            <Ionicons name="logo-twitter" size={22} color="#FFFFFF" />
                                        </View>
                                        <Text style={styles.socialCardLabel}>Twitter</Text>
                                    </TouchableOpacity>
                                )}
                                {mentor.socialLinks?.linkedin && (
                                    <TouchableOpacity
                                        style={styles.socialCard}
                                        onPress={() => handleContact('linkedin')}
                                    >
                                        <View style={[styles.socialCardGradient, { backgroundColor: '#0A66C2' }]}>
                                            <Ionicons name="logo-linkedin" size={22} color="#FFFFFF" />
                                        </View>
                                        <Text style={styles.socialCardLabel}>LinkedIn</Text>
                                    </TouchableOpacity>
                                )}
                            </View>
                        </View>
                    )}

                    {/* Availability Hint */}
                    <View style={styles.availabilityCard}>
                        <LinearGradient
                            colors={['#f0fdf4', '#dcfce7']}
                            style={styles.availabilityGradient}
                        >
                            <View style={styles.availabilityIcon}>
                                <Ionicons name="time-outline" size={24} color="#10B981" />
                            </View>
                            <View style={styles.availabilityInfo}>
                                <Text style={styles.availabilityTitle}>Available for Mentoring</Text>
                                <Text style={styles.availabilitySubtitle}>
                                    This mentor is currently accepting new mentees. Book a session to get started!
                                </Text>
                            </View>
                        </LinearGradient>
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Caption style={styles.footerText}>Youth Connect • Mentor Profile</Caption>
                    </View>
                </Animated.View>
            </Animated.ScrollView>

            {/* Floating Action Bar */}
            <View style={[styles.floatingBar, { paddingBottom: insets.bottom + 8 }]}>
                <TouchableOpacity
                    style={styles.floatingMainBtn}
                    onPress={() => handleContact('email')}
                >
                    <LinearGradient
                        colors={['#7c3aed', '#6d28d9']}
                        style={styles.floatingMainGradient}
                    >
                        <Ionicons name="send" size={18} color="#FFFFFF" />
                        <Text style={styles.floatingMainText}>Message {mentor.name?.split(' ')[0]}</Text>
                    </LinearGradient>
                </TouchableOpacity>
                <TouchableOpacity
                    style={styles.floatingSecondaryBtn}
                    onPress={() => navigation.navigate('MentorBooking', { mentor })}
                >
                    <Ionicons name="calendar" size={22} color={MENTOR_PRIMARY} />
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FAF5FF',
    },
    scrollView: {
        flex: 1,
    },

    // Hero
    heroSection: {
        height: 380,
        position: 'relative',
        overflow: 'hidden',
    },
    heroDecorOrb1: {
        position: 'absolute',
        width: 200,
        height: 200,
        borderRadius: 100,
        backgroundColor: 'rgba(196,181,253,0.15)',
        right: -60,
        top: -40,
    },
    heroDecorOrb2: {
        position: 'absolute',
        width: 100,
        height: 100,
        borderRadius: 50,
        backgroundColor: 'rgba(167,139,250,0.2)',
        left: -20,
        bottom: 40,
    },
    heroDecorOrb3: {
        position: 'absolute',
        width: 140,
        height: 140,
        borderRadius: 70,
        backgroundColor: 'rgba(76,29,149,0.15)',
        right: 30,
        bottom: -30,
    },
    heroContent: {
        flex: 1,
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingBottom: 24,
    },
    topBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    backBtn: {
        width: 42,
        height: 42,
        borderRadius: 14,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    shareBtn: {
        width: 42,
        height: 42,
        borderRadius: 14,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    profileContainer: {
        alignItems: 'center',
    },
    avatarOuterRing: {
        position: 'relative',
        marginBottom: 12,
    },
    avatar: {
        width: 100,
        height: 100,
        borderRadius: 50,
        borderWidth: 4,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    onlineDot: {
        position: 'absolute',
        bottom: 4,
        right: 4,
        width: 18,
        height: 18,
        borderRadius: 9,
        backgroundColor: '#10B981',
        borderWidth: 3,
        borderColor: '#5b21b6',
    },
    mentorName: {
        fontSize: 24,
        fontWeight: '800',
        color: '#FFFFFF',
        marginBottom: 6,
    },
    typeBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: 'rgba(255,255,255,0.15)',
        paddingHorizontal: 14,
        paddingVertical: 5,
        borderRadius: 14,
        marginBottom: 16,
    },
    typeText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 13,
        fontWeight: '600',
    },
    quickStats: {
        flexDirection: 'row',
        backgroundColor: 'rgba(255,255,255,0.12)',
        borderRadius: 16,
        paddingVertical: 12,
        paddingHorizontal: 20,
        gap: 0,
    },
    quickStatItem: {
        flex: 1,
        alignItems: 'center',
    },
    quickStatValue: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: '800',
    },
    quickStatLabel: {
        color: 'rgba(255,255,255,0.6)',
        fontSize: 11,
        fontWeight: '500',
        marginTop: 2,
    },
    quickStatDivider: {
        width: 1,
        backgroundColor: 'rgba(255,255,255,0.15)',
    },

    // Content
    contentSurface: {
        backgroundColor: '#FAF5FF',
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        marginTop: -24,
        paddingTop: 24,
        paddingHorizontal: 18,
    },

    // Actions
    actionRow: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 24,
    },
    primaryActionBtn: {
        flex: 2,
        borderRadius: 14,
        overflow: 'hidden',
        ...getShadow('md'),
    },
    primaryActionGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 14,
    },
    primaryActionText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '700',
    },
    secondaryActionBtn: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: MENTOR_PRIMARY + '30',
        paddingVertical: 14,
    },
    secondaryActionText: {
        color: MENTOR_PRIMARY,
        fontSize: 14,
        fontWeight: '700',
    },

    // Sections
    section: {
        marginBottom: 24,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
    },
    sectionIcon: {
        width: 36,
        height: 36,
        borderRadius: 12,
        alignItems: 'center',
        justifyContent: 'center',
    },
    sectionTitle: {
        fontSize: 17,
        fontWeight: '800',
        color: '#111827',
    },
    bioText: {
        fontSize: 14,
        color: '#4B5563',
        lineHeight: 22,
    },

    // Expertise
    expertiseTags: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    expertiseTag: {
        backgroundColor: '#7c3aed10',
        paddingHorizontal: 14,
        paddingVertical: 7,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#7c3aed20',
    },
    expertiseTagText: {
        fontSize: 13,
        fontWeight: '600',
        color: MENTOR_PRIMARY,
    },

    // Contact
    contactCards: {
        gap: 10,
    },
    contactCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        padding: 14,
        gap: 12,
        ...getShadow('sm'),
        borderWidth: 1,
        borderColor: '#F3F4F6',
    },
    contactCardIcon: {
        width: 44,
        height: 44,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    contactCardInfo: {
        flex: 1,
    },
    contactCardLabel: {
        fontSize: 11,
        fontWeight: '600',
        color: '#9CA3AF',
        marginBottom: 2,
    },
    contactCardValue: {
        fontSize: 14,
        fontWeight: '600',
        color: '#111827',
    },

    // Social
    socialGrid: {
        flexDirection: 'row',
        gap: 14,
    },
    socialCard: {
        alignItems: 'center',
        gap: 6,
    },
    socialCardGradient: {
        width: 52,
        height: 52,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
    },
    socialCardLabel: {
        fontSize: 11,
        fontWeight: '600',
        color: '#6B7280',
    },

    // Availability
    availabilityCard: {
        marginBottom: 24,
        borderRadius: 16,
        overflow: 'hidden',
        ...getShadow('sm'),
    },
    availabilityGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        gap: 14,
    },
    availabilityIcon: {
        width: 48,
        height: 48,
        borderRadius: 14,
        backgroundColor: 'rgba(16,185,129,0.12)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    availabilityInfo: {
        flex: 1,
    },
    availabilityTitle: {
        fontSize: 14,
        fontWeight: '700',
        color: '#065F46',
        marginBottom: 2,
    },
    availabilitySubtitle: {
        fontSize: 12,
        color: '#047857',
        lineHeight: 17,
    },

    // Footer
    footer: {
        alignItems: 'center',
        paddingVertical: 20,
    },
    footerText: {
        fontSize: 11,
        color: '#D1D5DB',
    },

    // Floating Bar
    floatingBar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        gap: 10,
        paddingHorizontal: 18,
        paddingTop: 12,
        backgroundColor: 'rgba(250,245,255,0.95)',
        borderTopWidth: 1,
        borderTopColor: '#F3E8FF',
    },
    floatingMainBtn: {
        flex: 1,
        borderRadius: 14,
        overflow: 'hidden',
        ...getShadow('lg'),
    },
    floatingMainGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 14,
    },
    floatingMainText: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '700',
    },
    floatingSecondaryBtn: {
        width: 52,
        height: 52,
        borderRadius: 14,
        backgroundColor: '#FFFFFF',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: MENTOR_PRIMARY + '30',
        ...getShadow('sm'),
    },
});

export default MentorDetailScreen;
